import { default as Swiper } from 'swiper';
import { Component } from '../../helpers/compiler';
import { getParentWithClass } from '../../helpers/dom/get-parent-with-class';

@Component({ className: 'js-gallery-slider', observe: true })
export class GallerySliderComponent {
    private instance: any;
    private header: HTMLElement | undefined;
    private interleaveOffset = 0.5;

    constructor(private element: HTMLElement, isCalledFromExternal = false, index = 0) {
        const wallTrigger = getParentWithClass(element, 'js-the-wall-trigger')
        this.header = element.querySelector('.js-gallery-slider-header') as HTMLElement;

        if (wallTrigger === null || wallTrigger === undefined) {
            const isInModal = this.element.getAttribute('data-in-modal') === 'true';

            if (isInModal) {
                if (isCalledFromExternal) {
                    this.init(true, index);
                    this.slideChange();
                }
            } else {
                this.init(false, index);
            }
        }
    }

    init(isInModal: boolean, index: number) {
        const wrapper = this.element.querySelector('.js-gallery-slider__wrapper') as HTMLElement;
        const pagination = this.element.querySelector('.js-gallery-slider__pagination') as HTMLElement;
        const navigation = this.element.querySelector('.js-gallery-slider__navigation') as HTMLElement;

        if (wrapper) {
            let navigationInstance = null;
            if (navigation) {
                const paginationNextEl = navigation.querySelector('.js-gallery-slider__navigation__next') as HTMLElement;
                const paginationPrevEl = navigation.querySelector('.js-gallery-slider__navigation__prev') as HTMLElement;

                navigationInstance = new Swiper(navigation, {
                    slidesPerView: isInModal ? 8 : 5,
                    centerInsufficientSlides: false,
                    spaceBetween: 10,
                    autoHeight: false,
                    initialSlide: index,
                    keyboard: {
                        enabled: true,
                        onlyInViewport: false,
                    },
                    watchSlidesProgress: true,
                    navigation: {
                        nextEl: paginationNextEl,
                        prevEl: paginationPrevEl
                    },
                    breakpoints: {
                        320: {
                            slidesPerView: 5,
                            spaceBetween: 10
                        },
                        768: {
                            slidesPerView: 6,
                            spaceBetween: 15
                        },
                        1024: {
                            slidesPerView: isInModal ? 7 : 5,
                            spaceBetween: isInModal ? 20 : 10
                        },
                        1230: {
                            slidesPerView: isInModal ? 8 : 5,
                            spaceBetween: isInModal ? 20 : 10
                        }
                    }
                });
            }

            this.instance = new Swiper(wrapper, {
                initialSlide: index,
                watchSlidesProgress: true,
                speed: 1000,
                navigation: {
                    nextEl: (this.element.querySelector('.js-gallery-slider__next') || this.element.querySelector('.swiper-button-next')) as HTMLElement,
                    prevEl: (this.element.querySelector('.js-gallery-slider__prev') || this.element.querySelector('.swiper-button-prev')) as HTMLElement
                },
                keyboard: {
                    enabled: true,
                    onlyInViewport: false,
                },
                on: {
                    slideChange: this.slideChange,
                    progress: this.progress,
                    touchStart: this.touchStart,
                    setTransition: this.setTransition
                },
                thumbs: navigationInstance != null ? { swiper: navigationInstance } : {}
            });

            document.addEventListener('lazyloaded', () => this.instance.update());

            if (screen.orientation && screen.orientation != undefined) {
                screen.orientation.addEventListener('change', () => this.instance.update());
            }
            else {
                window.addEventListener('orientationchange', () => this.instance.update());
            }
        }
    }

    slideChange = () => {
        if (this.instance && this.instance.slides) {
            const previousItem = this.instance.slides[this.instance.previousIndex];
            const activeItem = this.instance.slides[this.instance.activeIndex];

            if (this.header) {
                this.header.innerHTML = activeItem.getAttribute('data-gallery-slide-title')
            }

            if (previousItem || activeItem) {
                this.startIFrame(activeItem);
                this.stopIFrame(previousItem);
            }
        }
    };

    progress = (swiper: Swiper) => {
        for (let i = 0; i < swiper.slides.length; i++) {
            const slide = swiper.slides[i];
            const slideProgress = slide.progress || 0;
            const innerOffset = swiper.width * this.interleaveOffset;
            const innerTranslate = slideProgress * innerOffset;
            const slideInner = slide.querySelector(".js-gallery-slider-inner") as HTMLElement;
            if (slideInner) {
                slideInner.style.transform = "translate3d(" + innerTranslate + "px, 0, 0)";
            }
        }
    }

    touchStart = (swiper: Swiper) => {
        for (let i = 0; i < swiper.slides.length; i++) {
            swiper.slides[i].style.transition = "";
        }
    }

    setTransition = (swiper: Swiper, speed: number) => {
        for (let i = 0; i < swiper.slides.length; i++) {
            const slide = swiper.slides[i] as HTMLElement;
            slide.style.transition = speed + "ms";
            const slideInner = slide.querySelector(".js-gallery-slider-inner") as HTMLElement;
            if (slideInner) {
                slideInner.style.transition = speed + "ms";
            }
        }
    }

    destroy() {
        this.stopAllIFrames(this.element);
        this.instance.destroy();
    }

    private stopIFrame(el: HTMLElement) {
        const iframe = this.findIFrame(el);
        if (iframe) {
            iframe.setAttribute('src', '');
        }
    }

    private stopAllIFrames(el: HTMLElement) {
        if (el.parentElement) {
            this.findAllIFrames(el)?.forEach(e => this.stopIFrame(e.parentElement!));
        }
    }

    private startIFrame(el: HTMLElement) {
        const iframe = this.findIFrame(el);
        if (iframe) {
            let src = iframe.getAttribute('data-src') || '';
            src = src.replace('autoplay=0', 'autoplay=1');
            iframe.setAttribute('src', src);
        }
    }

    private findIFrame(el: HTMLElement): Element | undefined {
        if (el) {
            return el.querySelector('iframe') as Element;
        }
    }

    private findAllIFrames(el: HTMLElement): Element[] | undefined {
        if (el) {
            return Array.from(el.querySelectorAll('iframe')) as HTMLElement[];
        }
    }
}
